import { useEffect, useRef, useState, memo } from "react";
import { useTranslation } from "react-i18next";
import "./style.css";

// Защита: возвращает массив в любом случае
const toArray = (val, fallback = []) => (Array.isArray(val) ? val : fallback);

// LivePreview and ProcessCard take no props and read their own
// translations -- nothing about them depends on HeroSection's `typed`
// state. Without memo, every keystroke of the typed-text effect (every
// 60-100ms) re-rendered both anyway, measured via React Profiler at ~30
// renders each per 5s window. memo() with no props to compare means they
// only ever re-render for their own state/context changes.

// ─── LivePreview ─────────────────────────────────────────────────────────────
const LivePreview = memo(function LivePreview() {
  const { t } = useTranslation("hero");
  const services = toArray(t("services", { returnObjects: true }), [
    { title: "Websites", slug: "websites", text: "", accent: "#ff7a3c" },
  ]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [canRotate, setCanRotate] = useState(false);

  // Rotating this preview swaps in a new block of text. Trace analysis
  // (largestContentfulPaint::Candidate events) showed that the FIRST
  // automatic rotation -- which used to fire a flat 4000ms after mount,
  // regardless of how the page was doing -- was itself getting recorded
  // as a new, larger LCP candidate, overtaking whatever had legitimately
  // painted first (the cookie banner text, in our measurements).
  //
  // This is NOT an animation/repaint-identity quirk: removing the opacity
  // transition, and separately removing the transform entirely, changed
  // nothing -- Chrome records a new LCP candidate for *any* visible text
  // mutation that renders bigger than the current one, animated or not.
  // A follow-up attempt to instead just delay the first rotation (wait
  // for the native LargestContentfulPaint observer to go quiet for 2s,
  // matching how real CrUX field data is expected to settle) *also*
  // failed under measurement: the rotation-triggered candidate still
  // showed up, just ~2s later (confirmed via a second round of 3x
  // real-throttled Lighthouse + trace analysis). A timer can only push
  // the mutation later, never make it provably safe -- real-world LCP
  // tracking has no fixed cutoff; it only stops when the browser sees
  // an actual user interaction or the tab is hidden.
  //
  // Gating on that same real signal is the only variant that survived
  // measurement: the browser itself stops accumulating LCP candidates
  // the moment the user scrolls, taps, presses a key, or touches the
  // screen, so starting the rotation on that same first interaction can
  // never be blamed for a late LCP -- by definition, the browser has
  // already stopped looking by then. The trade-off (accepted rather than
  // masked): a visitor who never interacts at all during their visit
  // won't see the preview auto-rotate. Given how easily interaction
  // happens in practice -- e.g. any user scrolling to see the rest of the
  // page -- the passive-showcase behavior is preserved for the
  // overwhelming majority of real visits.
  useEffect(() => {
    if (services.length < 2) return undefined;

    const INTERACTION_EVENTS = ['scroll', 'pointerdown', 'keydown', 'touchstart'];

    const unlock = () => setCanRotate(true);

    INTERACTION_EVENTS.forEach((evt) =>
      window.addEventListener(evt, unlock, { once: true, passive: true })
    );

    return () => {
      INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, unlock));
    };
  }, [services.length]);

  useEffect(() => {
    if (!canRotate || services.length < 2) return undefined;
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % services.length);
        setIsAnimating(false);
      }, 260);
    }, 4000);
    return () => clearInterval(interval);
  }, [canRotate, services.length]);

  const safeIndex = activeIndex % services.length;
  const active = services[safeIndex];

  const handleDotClick = (i) => {
    if (i === safeIndex) return;
    setIsAnimating(true);
    setTimeout(() => {
      setActiveIndex(i);
      setIsAnimating(false);
    }, 260);
  };

  const metrics = toArray(t("livePreview.metrics", { returnObjects: true }));

  return (
    <div className="hp-live">
      <div className="hp-live__header">
        <span className="hp-live__badge">{t("livePreview.badge")}</span>

        <div className="hp-live__indicators">
          {services.map((service, i) => (
            <button
              key={service.title}
              type="button"
              className={`hp-live__dot ${i === safeIndex ? "hp-live__dot--active" : ""}`}
              onClick={() => handleDotClick(i)}
              style={{
                backgroundColor:
                  i === safeIndex ? service.accent : "rgba(255,255,255,0.2)",
              }}
              aria-label={`${t("livePreview.showAriaLabel")} ${service.title}`}
            />
          ))}
        </div>
      </div>

      <div
        className={`hp-live__preview ${isAnimating ? "hp-live__preview--exiting" : ""}`}
        style={{ "--accent": active.accent }}
      >
        <div className="hp-live__mockup">
          <div className="hp-live__mockup-header">
            <div className="hp-live__mockup-dots">
              <span style={{ backgroundColor: "#ff5f56" }} />
              <span style={{ backgroundColor: "#ffbd2e" }} />
              <span style={{ backgroundColor: "#27c93f" }} />
            </div>
            <div className="hp-live__mockup-url">
              pixelcraft/{active.slug}
            </div>
          </div>

          <div className="hp-live__mockup-content">
            <div
              className="hp-live__mockup-title"
              style={{ color: active.accent }}
            >
              {active.title}
            </div>
            <div className="hp-live__mockup-text">{active.text}</div>

            <div className="hp-live__mockup-progress">
              <div className="hp-live__mockup-bar">
                <div
                  className="hp-live__mockup-fill"
                  style={{
                    width: `${((safeIndex + 1) / services.length) * 100}%`,
                    backgroundColor: active.accent,
                  }}
                />
              </div>
              <span className="hp-live__mockup-stats">
                {safeIndex + 1}/{services.length}{" "}
                {t("livePreview.focusAreas")}
              </span>
            </div>
          </div>
        </div>

        <div className="hp-live__metrics">
          {metrics.map((m) => (
            <div key={m.label} className="hp-live__metric">
              <span
                className="hp-live__metric-value"
                style={{ color: active.accent }}
              >
                {m.value}
              </span>
              <span className="hp-live__metric-label">{m.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

// ─── ProcessCard ──────────────────────────────────────────────────────────────
const ProcessCard = memo(function ProcessCard() {
  const { t } = useTranslation("hero");
  const steps = toArray(t("process.steps", { returnObjects: true }));

  return (
    <div className="hp-process">
      <div className="hp-process__label">{t("process.label")}</div>

      <div className="hp-process__list">
        {steps.map((item) => (
          <div key={item.number} className="hp-process__item">
            <div className="hp-process__top">
              <span className="hp-process__number">{item.number}</span>
              <span className="hp-process__title">{item.title}</span>
            </div>
            <p className="hp-process__text">{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
});

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function HeroSection() {
  const { t } = useTranslation("hero");

  const typingWords = toArray(t("typingWords", { returnObjects: true }), [
    "Designer",
  ]);
  const stack = toArray(t("stack", { returnObjects: true }));

  // window.__PRERENDERING__ is set only by scripts/build/prerender.js,
  // before any app code runs, never present for a real visitor (same guard
  // TypedText.js uses). It's read here for one reason: the *initial*
  // `typed` value below must be the same in the prerendered snapshot and
  // in hydrateRoot()'s first client render, or hydration sees a text
  // mismatch. Without this, the previous version started at "" always and
  // relied on prerender.js's page capture happening within 600ms (this
  // effect's initial delay) of mount -- true often enough in earlier
  // testing, but a genuine, confirmed (not just theoretical) race: once
  // the capture takes 600ms+ (a more loaded machine), the saved HTML ends
  // up with a partial, non-deterministic substring ("V", "Ve", ...) that
  // can never match a fresh hydrate's "".
  const isPrerendering = typeof window !== "undefined" && window.__PRERENDERING__ === true;

  const firstWord = typingWords[0] || "";
  const [typed, setTyped] = useState(firstWord);
  const strIdx = useRef(0);
  // Both renders (prerendered and first hydrate) now start already showing
  // `firstWord` in full -- these refs start "as if" the typing pass for it
  // already finished, so the first tick continues into the backspacing
  // phase instead of restarting from an empty string (which would cause a
  // visible flash: full word -> blank -> retyped).
  const charIdx = useRef(firstWord.length);
  const deleting = useRef(true);
  const wordsRef = useRef(typingWords);
  const mounted = useRef(false);

  // Обновляем ref при смене языка
  useEffect(() => {
    wordsRef.current = typingWords;
  }, [typingWords]);

  // Сброс анимации при смене языка (not on initial mount -- see the refs above)
  const wordsKey = typingWords.join(",");
  useEffect(() => {
    if (isPrerendering) return;
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    strIdx.current = 0;
    charIdx.current = 0;
    deleting.current = false;
    setTyped("");
  }, [wordsKey, isPrerendering]);

  // Основной typed-эффект
  useEffect(() => {
    if (isPrerendering) return undefined;
    let timeoutId;

    const tick = () => {
      const words = wordsRef.current;
      if (!words.length) return;

      const current = words[strIdx.current % words.length];

      if (!deleting.current) {
        charIdx.current += 1;
        setTyped(current.slice(0, charIdx.current));

        if (charIdx.current === current.length) {
          deleting.current = true;
          timeoutId = setTimeout(tick, 1800);
          return;
        }
      } else {
        charIdx.current -= 1;
        setTyped(current.slice(0, charIdx.current));

        if (charIdx.current === 0) {
          deleting.current = false;
          strIdx.current = (strIdx.current + 1) % words.length;
        }
      }

      timeoutId = setTimeout(tick, deleting.current ? 60 : 100);
    };

    timeoutId = setTimeout(tick, 600);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrerendering]);

  return (
    <div className="hero-proposal">
      <section className="hp-hero">
        {/* ── LEFT ── */}
        <div className="hp-hero__left">
          <div className="hp-hero__status">
            <div className="hp-hero__status-dot" />
            <span className="hp-hero__status-text">{t("status")}</span>
          </div>

          <div className="hp-hero__eyebrow">{t("eyebrow")}</div>

          <h1 className="hp-hero__title">
            {t("titlePrefix")} <em>{t("titleName")}</em>
          </h1>

          <div className="hp-hero__typed-row">
            <span className="hp-hero__typed">{typed}</span>
            <span className="hp-hero__cursor" />
          </div>

          <p className="hp-hero__text">{t("text")}</p>

          <div className="hp-hero__cta">
            <a href="/contacts" className="hp-btn button">
              {t("ctaButton")}
            </a>
          </div>
        </div>

        {/* ── RIGHT ── */}
        <div className="hp-hero__right">
          <LivePreview />
          <ProcessCard />

          <div className="hp-stack">
            {stack.map((item) => (
              <span key={item} className="hp-stack__chip">
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}